import { FieldValidator, useField, useFormikContext } from 'formik';
import { forwardRef } from 'react';

import { composeValidators, required } from 'shared/utils/form';

import { Select, SelectProps } from '../Select';
import { getIsInvalid } from '../common';

export interface FormSelectProps extends Omit<SelectProps, 'inputProps'> {
  /** Name of the field. */
  name?: string;

  validate?: FieldValidator;

  /** @deprecated Use `name`, `validate` or `required` instead. */
  fieldProps?: {
    name: string;
    validate?: FieldValidator;
    /** @deprecated Use `onChange` directly into the component instead. */
    onChange?: SelectProps['onChange'];
    /** @deprecated Use `required` directly into the component instead. */
    required?: boolean;
  };

  /** @deprecated Use normal props instead. */
  selectProps?: SelectProps;
}

export const FormSelect = forwardRef<HTMLDivElement, FormSelectProps>(
  (
    {
      fieldProps,
      selectProps = {},
      name = fieldProps?.name,
      required: isRequired = fieldProps?.required ?? selectProps?.required,
      validate,
      children,
      ...props
    },
    ref,
  ) => {
    const validation = composeValidators(
      isRequired ? required : undefined,
      validate ?? fieldProps?.validate,
    );

    const [field, meta] = useField({
      name: name as string,
      validate: validation,
    });

    const form = useFormikContext();

    const hasError = getIsInvalid({ meta, form });
    const message = hasError ? meta.error : undefined;

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
      field.onChange(event);
      props.onChange?.(event, event.currentTarget);
      fieldProps?.onChange?.(event, event.currentTarget);
      selectProps?.onChange?.(event, event.currentTarget);
    };

    return (
      <Select
        ref={ref}
        helperText={message}
        variant="outlined"
        {...props}
        {...selectProps}
        name={name}
        value={field.value ?? ''}
        onChange={handleChange}
        error={hasError}
        required={isRequired}
      >
        {children ?? selectProps.children}
      </Select>
    );
  },
);
